import {
    Component,
    OnInit,
    ChangeDetectorRef,
    signal,
    ChangeDetectionStrategy,
    Output,
    EventEmitter,
    computed,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25TableModule } from "../../../s25-table/s25.table.module";
import { Table } from "../../../s25-table/Table";
import { S25EditableModule } from "../../../s25-editable/s25.editable.module";
import { BudgetService } from "../budget.service";
import { Budget } from "../Budget";
import { S25Util } from "../../../../util/s25-util";
import { Bind } from "../../../../decorators/bind.decorator";
import { S25LoadingInlineModule } from "../../../s25-loading/s25.loading.inline.module";
import { CacheRepository } from "../../../../decorators/cache.decorator";
import { GenericTableButtonComponent } from "../../../s25-table/generics/generic.table.button.component";
import { UserprefService } from "../../../../services/userpref.service";
import { SeSecurityStore } from "../../se.security.store";
import { inject } from "@angular/core";

@TypeManagerDecorator("s25-ng-fiscal-year-management")
@Component({
    selector: "s25-ng-fiscal-year-management",
    template: `
        <div class="fiscal-year-management-container">
            <div class="management-header">
                <h3>Fiscal Year Management</h3>
                @if (canManageFiscalYears()) {
                    <s25-ng-button
                        [type]="'primary'"
                        [buttonText]="'+ Add Fiscal Year'"
                        [onClick]="startAddFiscalYear"
                        [disabled]="isProcessing()"
                    ></s25-ng-button>
                }
            </div>

            <!-- <s25-ng-fading-success-msg [message]="successMessage"></s25-ng-fading-success-msg> -->
            @if (isLoading()) {
                <s25-ng-loading-inline-static [text]="'Loading fiscal years...'"></s25-ng-loading-inline-static>
            }

            <!-- Error State -->
            @if (error) {
                <div class="error-state">
                    <p class="error-message">{{ error }}</p>
                    <s25-ng-button
                        [type]="'outline'"
                        [buttonText]="'Retry'"
                        [onClick]="loadFiscalYears"
                    ></s25-ng-button>
                </div>
            }

            <!-- Fiscal Year Form Modal -->
            @if (showForm()) {
                <div class="modal-overlay" (click)="cancelForm()">
                    <div class="modal-content" (click)="$event.stopPropagation()">
                        <div class="modal-header">
                            <h4>{{ isEditing() ? "Edit Fiscal Year" : "Add New Fiscal Year" }}</h4>
                            <button class="close-button" (click)="cancelForm()" type="button">×</button>
                        </div>

                        <form class="fiscal-year-form" (ngSubmit)="saveFiscalYear()" #fiscalYearForm="ngForm">
                            <div class="form-group">
                                <label for="fiscalYearName">Fiscal Year Name *</label>
                                <input
                                    id="fiscalYearName"
                                    type="text"
                                    [(ngModel)]="formData.name"
                                    name="fiscalYearName"
                                    required
                                    maxlength="100"
                                    class="form-input"
                                    placeholder="e.g., 2024-2025"
                                />
                            </div>

                            <div class="form-group">
                                <label for="fiscalYearDescription">Description</label>
                                <textarea
                                    id="fiscalYearDescription"
                                    [(ngModel)]="formData.description"
                                    name="fiscalYearDescription"
                                    class="form-textarea"
                                    placeholder="Optional description"
                                    rows="3"
                                ></textarea>
                            </div>

                            <div class="form-row">
                                <div class="form-group">
                                    <label for="startDate">Start Date *</label>
                                    <s25-ng-editable-date
                                        [(val)]="formData.startDate"
                                        [placeholder]="'Select start date'"
                                        class="date-input"
                                    ></s25-ng-editable-date>
                                </div>

                                <div class="form-group">
                                    <label for="endDate">End Date *</label>
                                    <s25-ng-editable-date
                                        [(val)]="formData.endDate"
                                        [placeholder]="'Select end date'"
                                        class="date-input"
                                    ></s25-ng-editable-date>
                                </div>
                            </div>

                            <div class="form-actions">
                                <s25-ng-button
                                    [type]="'outline'"
                                    [buttonText]="'Cancel'"
                                    [onClick]="cancelForm"
                                    [disabled]="isProcessing()"
                                ></s25-ng-button>
                                <s25-ng-button
                                    [type]="'primary'"
                                    [buttonText]="isEditing() ? 'Update Fiscal Year' : 'Create Fiscal Year'"
                                    [onClick]="saveFiscalYear"
                                    [isLoading]="isProcessing()"
                                    [disabled]="!fiscalYearForm.valid"
                                ></s25-ng-button>
                            </div>
                        </form>
                    </div>
                </div>
            }

            <!-- Fiscal Years Table -->
            @if (!isLoading() && !error && fiscalYearTableSource()) {
                <s25-ng-table
                    [dataSource]="fiscalYearTableSource()!"
                    [hasRefresh]="true"
                    [hasFilter]="true"
                    [columnSortable]="true"
                    [hasTotalRowCount]="true"
                    [caption]="'Fiscal Years'"
                    [noResultsMessage]="'Click Add Fiscal Year to create your first fiscal year.'"
                ></s25-ng-table>
            }

            <!-- Delete Confirmation Modal -->
            @if (showDeleteConfirm()) {
                <div class="modal-overlay" (click)="cancelDelete()">
                    <div class="modal-content small" (click)="$event.stopPropagation()">
                        <div class="modal-header">
                            <h4>Confirm Delete</h4>
                        </div>
                        <div class="modal-body">
                            <p>Are you sure you want to delete the fiscal year "{{ fiscalYearToDelete()?.name }}"?</p>
                            <p class="warning-text">This action cannot be undone.</p>
                        </div>
                        <div class="modal-actions">
                            <s25-ng-button
                                [type]="'outline'"
                                [buttonText]="'Cancel'"
                                [onClick]="cancelDelete"
                            ></s25-ng-button>
                            <s25-ng-button
                                [type]="'danger'"
                                [buttonText]="'Delete'"
                                [onClick]="deleteFiscalYear"
                                [isLoading]="isProcessing()"
                            ></s25-ng-button>
                        </div>
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        .fiscal-year-management-container {
            padding: 1.5rem;
        }

        .management-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid var(--color--border);
        }

        .management-header h3 {
            margin: 0;
            color: var(--color--text);
            font-size: 1.25rem;
            font-weight: 600;
        }

        .error-state {
            text-align: center;
            padding: 2rem;
            background: var(--color--surface-bg-error);
            border: 1px solid color-mix(in srgb, var(--color--danger) 35%, var(--color--surface-bg));
            border-radius: 8px;
            margin: 1rem 0;
        }

        .error-message {
            color: var(--color--danger);
            margin-bottom: 1rem;
        }

        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: color-mix(in srgb, black 50%, transparent);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        }

        .modal-content {
            background: var(--color--surface-bg);
            border-radius: 8px;
            box-shadow: 0 4px 20px color-mix(in srgb, var(--color--text) 15%, transparent);
            max-width: 600px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
        }

        .modal-content.small {
            max-width: 400px;
        }

        .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.5rem;
            border-bottom: 1px solid var(--color--border);
        }

        .modal-header h4 {
            margin: 0;
            color: var(--color--text);
            font-size: 1.1rem;
            font-weight: 600;
        }

        .close-button {
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: var(--color--text-muted);
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .close-button:hover {
            color: var(--color--text);
        }

        .fiscal-year-form {
            padding: 1.5rem;
        }

        .form-group {
            margin-bottom: 1rem;
        }

        .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
            margin-bottom: 1rem;
        }

        .form-group label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
            color: var(--color--text);
        }

        .form-input,
        .form-textarea,
        .form-select {
            width: 100%;
            padding: 0.75rem;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            font-size: 1rem;
            transition: border-color 0.2s;
            background: var(--color--surface-bg);
            color: var(--color--text);
        }

        .form-input:focus,
        .form-textarea:focus,
        .form-select:focus {
            outline: none;
            border-color: var(--cn-blue);
            box-shadow: 0 0 0 2px color-mix(in srgb, var(--cn-blue) 28%, transparent);
        }

        .checkbox-label {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            cursor: pointer;
        }

        .form-checkbox {
            width: 18px;
            height: 18px;
            cursor: pointer;
        }

        .form-actions {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
            margin-top: 1.5rem;
            padding-top: 1rem;
            border-top: 1px solid var(--color--border);
        }

        .modal-body {
            padding: 1.5rem;
        }

        .modal-actions {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
            padding: 1rem 1.5rem;
            border-top: 1px solid var(--color--border);
        }

        .warning-text {
            color: var(--color--danger);
            font-weight: 500;
            margin-top: 0.5rem;
        }

        @media (max-width: 768px) {
            .fiscal-year-management-container {
                padding: 1rem;
            }

            .management-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
            }

            .modal-content {
                width: 95%;
                margin: 1rem;
            }

            .form-row {
                grid-template-columns: 1fr;
            }
        }
    `,
    standalone: true,
    imports: [FormsModule, S25ButtonComponent, S25LoadingInlineModule, S25TableModule, S25EditableModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiscalYearManagementComponent implements OnInit {
    @Output() fiscalYearsChanged = new EventEmitter<void>();

    private securityStore = inject(SeSecurityStore);

    isLoading = signal(true);
    isProcessing = signal(false);
    showForm = signal(false);
    isEditing = signal(false);
    showDeleteConfirm = signal(false);

    fiscalYears = signal<Budget.FiscalYear[]>([]);
    fiscalYearToDelete = signal<Budget.FiscalYear | null>(null);
    s25DateTimeFormat: string = "MM/dd/yyyy";
    readonly fiscalYearTableSource = signal<Table.DataSource | undefined>(undefined);
    successMessage: string = "";
    error: string | null = null;

    formData: Partial<Budget.FiscalYear> = {
        name: "",
        description: "",
        startDate: undefined,
        endDate: undefined,
    };

    constructor(private cd: ChangeDetectorRef) {}

    canManageFiscalYears = computed(() => this.securityStore.hasFLSOverride());

    async ngOnInit(): Promise<void> {
        await this.securityStore.ensureInitialized();
        await Promise.all([this.loadFiscalYears(), this.initializeTableData()]);
    }

    private async initializeTableData(): Promise<void> {
        this.s25DateTimeFormat = await UserprefService.getS25Dateformat();

        this.fiscalYearTableSource.set({
            type: "unpaginated",
            dataSource: this.getFiscalYearTableRows,
            columns: [
                {
                    id: "name",
                    header: "Fiscal Year",
                    cells: {
                        text: "",
                    },
                },
                {
                    id: "dateRange",
                    header: "Date Range",
                    cells: {
                        text: "",
                    },
                },
                ...(this.canManageFiscalYears()
                    ? [
                          {
                              id: "edit",
                              header: "Edit",
                              width: 70,
                              align: "center",
                              sortable: false,
                              cells: {
                                  component: GenericTableButtonComponent,
                                  inputs: {
                                      label: "Edit",
                                      type: "outline",
                                  },
                              } satisfies Table.Cell<GenericTableButtonComponent>,
                          },
                          {
                              id: "delete",
                              header: "Delete",
                              width: 70,
                              align: "center",
                              sortable: false,
                              cells: {
                                  component: GenericTableButtonComponent,
                                  inputs: {
                                      label: "Delete",
                                      type: "danger--outline",
                                  },
                              } satisfies Table.Cell<GenericTableButtonComponent>,
                          },
                      ]
                    : []),
            ],
        });
    }

    @Bind
    async getFiscalYearTableRows(): Promise<Table.DataSourceResponse> {
        CacheRepository.invalidateByService("BudgetService", "getFiscalYears");
        const fiscalYears = this.fiscalYears();
        const rows: Table.Row[] = [];

        for (const fiscalYear of fiscalYears) {
            rows.push(this.createFiscalYearRow(fiscalYear));
        }

        return {
            rows,
            totalRows: rows.length,
        };
    }

    private createFiscalYearRow(fiscalYear: Budget.FiscalYear): Table.Row {
        const cells: Record<string, Table.Cell> = {
            name: {
                text: fiscalYear.name,
            },
            dateRange: {
                text: S25Util.date.fromToString(fiscalYear.startDate, fiscalYear.endDate, this.s25DateTimeFormat),
            },
        };

        // Only add edit/delete cells if user has permission
        if (this.canManageFiscalYears()) {
            cells.edit = {
                inputs: {
                    label: "Edit",
                    onClick: () => this.startEditFiscalYear(fiscalYear),
                },
            };
            cells.delete = {
                inputs: {
                    label: "Delete",
                    onClick: () => this.confirmDeleteFiscalYear(fiscalYear),
                },
            };
        }

        return {
            id: fiscalYear.fiscalYearId,
            name: fiscalYear.name,
            data: fiscalYear,
            cells,
        };
    }

    async loadFiscalYears(): Promise<void> {
        this.isLoading.set(true);
        this.error = null;

        try {
            // Invalidate cache to ensure fresh data
            CacheRepository.invalidateByService("BudgetService", "getFiscalYears");
            const [fiscalYears, error] = await S25Util.Maybe(BudgetService.getFiscalYears());

            if (error) {
                this.error = "Failed to load fiscal years. Please try again.";
                console.error("Error loading fiscal years:", error);
            } else {
                this.fiscalYears.set(fiscalYears || []);
            }
        } catch (error) {
            this.error = "An unexpected error occurred while loading fiscal years.";
            console.error("Unexpected error:", error);
        } finally {
            this.isLoading.set(false);
            this.cd.detectChanges();
        }
    }

    startAddFiscalYear = () => {
        this.resetForm();
        this.isEditing.set(false);
        this.showForm.set(true);
    };

    startEditFiscalYear = (fiscalYear: Budget.FiscalYear) => {
        this.formData = {
            ...fiscalYear,
            startDate: S25Util.date.toS25ISODateTimeStr(fiscalYear.startDate),
            endDate: S25Util.date.toS25ISODateTimeStr(fiscalYear.endDate),
        };
        this.isEditing.set(true);
        this.showForm.set(true);
        this.cd.detectChanges();
    };

    @Bind
    async saveFiscalYear(): Promise<void> {
        if (this.isProcessing()) return;
        this.isProcessing.set(true);
        this.cd.detectChanges();

        try {
            const fiscalYearData: Budget.FiscalYear = {
                fiscalYearId: this.formData.fiscalYearId!,
                name: this.formData.name!,
                description: this.formData.description,
                startDate: S25Util.date.toS25ISODateTimeStr(this.formData.startDate!)!,
                endDate: S25Util.date.toS25ISODateTimeStr(this.formData.endDate!)!,
            };

            if (this.isEditing()) {
                const [result, error] = await S25Util.Maybe(
                    BudgetService.updateFiscalYear(this.formData.fiscalYearId!, fiscalYearData),
                );
                if (error) throw error;
                this.successMessage = "Fiscal year updated successfully";
            } else {
                const [result, error] = await S25Util.Maybe(BudgetService.createFiscalYear(fiscalYearData));
                if (error) throw error;
                this.successMessage = "Fiscal year created successfully";
            }

            await this.loadFiscalYears();
            this.showForm.set(false);
            this.fiscalYearsChanged.emit();
        } catch (error) {
            S25Util.showError(`Failed to save fiscal year: ${S25Util.errorText(error)}`);
        } finally {
            this.isProcessing.set(false);
            this.cd.detectChanges();
        }
    }

    cancelForm = () => {
        this.showForm.set(false);
        this.resetForm();
    };

    confirmDeleteFiscalYear = (fiscalYear: Budget.FiscalYear) => {
        this.fiscalYearToDelete.set(fiscalYear);
        this.showDeleteConfirm.set(true);
        this.cd.detectChanges();
    };

    @Bind
    async deleteFiscalYear(): Promise<void> {
        const fiscalYear = this.fiscalYearToDelete();
        if (!fiscalYear || this.isProcessing()) return;

        this.isProcessing.set(true);
        this.cd.detectChanges();

        try {
            const [result, error] = await S25Util.Maybe(BudgetService.deleteFiscalYear(fiscalYear.fiscalYearId));
            if (error) throw error;

            this.successMessage = "Fiscal year deleted successfully";
            await this.loadFiscalYears();
            this.showDeleteConfirm.set(false);
            this.fiscalYearToDelete.set(null);
            this.fiscalYearsChanged.emit();
        } catch (error) {
            S25Util.showError(`Failed to delete fiscal year: ${S25Util.errorText(error)}`);
        } finally {
            this.isProcessing.set(false);
            this.cd.detectChanges();
        }
    }

    cancelDelete = () => {
        this.showDeleteConfirm.set(false);
        this.fiscalYearToDelete.set(null);
    };

    private resetForm(): void {
        this.formData = {
            name: "",
            description: "",
            startDate: undefined,
            endDate: undefined,
        };
    }
}
